import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProductSelector from './ProductSelector';
import '../styles/PostAnalytics.css';
import { apiPost } from '../services/apiClient';
import { fillBucketGaps } from '../utils/timeSeriesBucketing';
import { formatCompactNumber, formatFullNumber } from '../utils/formatNumber';

// Engagement split by post sentiment (NPS): a diverging bar chart with positive
// engagement (posts rated NPS > 6) above the x-axis in green and negative
// engagement (NPS < 4) below in red. Reads /topic/engagement/by-sentiment, which
// serves the precomputed topic_engagement_by_sentiment_daily table. Built
// ALONGSIDE the other trackers; touches nothing existing.

const BUCKET_PERIOD_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
];

const POSITIVE_COLOR = '#4caf50';
const NEGATIVE_COLOR = '#e53935';

// Bucket daily {fullDate, positive, negative} rows into day/week/month, summing
// both measures. Mirrors the week (Sunday-start) / month grouping used by the
// engagement chart's timeSeriesBucketing util, generalized to two fields.
function bucketSentiment(rows, bucketPeriod) {
    if (bucketPeriod === 'day') return rows;

    const keyFor = (fullDate) => {
        const d = parseISO(fullDate);
        if (bucketPeriod === 'week') {
            const weekStart = new Date(d);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay());
            return { key: format(weekStart, 'yyyy-MM-dd'), start: weekStart, label: format(weekStart, 'MMM dd') };
        }
        // month
        const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
        return { key: format(monthStart, 'yyyy-MM'), start: monthStart, label: format(monthStart, 'MMM yyyy') };
    };

    const buckets = new Map();
    rows.forEach(r => {
        const { key, start, label } = keyFor(r.fullDate);
        if (!buckets.has(key)) {
            buckets.set(key, { date: label, fullDate: format(start, 'yyyy-MM-dd'), positive: 0, negative: 0, _t: start.getTime() });
        }
        const b = buckets.get(key);
        b.positive += r.positive;
        b.negative += r.negative;
    });

    return Array.from(buckets.values()).sort((a, b) => a._t - b._t);
}

const PostSentimentEngagementAnalytics = ({ topicMap, companyName, autoSearch = false,
    initialStartDate, initialEndDate, initialBucket,
    // metric = 'likes' | 'views'. Defaults preserve the likes-by-sentiment chart;
    // metric="views" points at /topic/views/by-sentiment and its *_views fields.
    metric = 'likes', chartTitle = 'Engagement by Sentiment', note = '',
    // When set (e.g. "youtube") AND metric="views", restrict to a single source
    // via /topic/views/by-sentiment/by-source (the combined by-source-by-sentiment
    // table). Undefined = all sources, as before.
    sourceFilter,
    // Embedded mode: hide own control bar; driven by a shared parent control bar
    // (controlled topic/date/bucket + auto-fetch on change).
    embedded = false,
    startDate: controlledStartDate, endDate: controlledEndDate,
    bucketPeriod: controlledBucketPeriod,
    // Zero-fill empty buckets across the range (opt-in) and shrink the plot so
    // several charts stack into view at once.
    fillEmptyBuckets = false, chartHeight = 500,
    // Compact the value-axis ticks (1.2K / 5M). Opt-in so shared uses (Daily
    // Trackers) are unchanged; the YouTube Analytics page sets it for views.
    compactYAxis = false,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    // Endpoint + response field names derived from the metric (+ sourceFilter).
    const endpoint = (sourceFilter && metric === 'views')
        ? 'topic/views/by-sentiment/by-source'
        : (metric === 'views' ? 'topic/views/by-sentiment' : 'topic/engagement/by-sentiment');
    const positiveField = `positive_${metric}`;
    const negativeField = `negative_${metric}`;
    const metricNoun = metric === 'views' ? 'Views' : 'Likes';
    const [data, setData] = useState([]);
    const [coverage, setCoverage] = useState(null);
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [internalBucketPeriod, setBucketPeriod] = useState(initialBucket || 'month');
    const bucketPeriod = controlledBucketPeriod !== undefined ? controlledBucketPeriod : internalBucketPeriod;
    const [isLoading, setIsLoading] = useState(false);

    const today = new Date();
    const [internalStartDate, setStartDate] = useState(initialStartDate || '2024-01-01');
    const startDate = controlledStartDate !== undefined ? controlledStartDate : internalStartDate;
    const [internalEndDate, setEndDate] = useState(initialEndDate || today.toISOString().split('T')[0]);
    const endDate = controlledEndDate !== undefined ? controlledEndDate : internalEndDate;
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};
        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });
            if (sortedTopics[0]) setSelectedProduct(sortedTopics[0].id);
        }
        return { ...topicMap, 'all': { id: 'all', name: 'All Topics', priority: 9999 } };
    }, [topicMap, selectedProduct]);

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getBucketLabel = () => {
        const bucket = BUCKET_PERIOD_OPTIONS.find(b => b.value === bucketPeriod);
        return bucket ? bucket.label : 'Daily';
    };

    const buildMainTitle = () => `${getSelectedProductName()} · ${chartTitle}`;

    const buildSubline = () => {
        const dateRange = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        return `${dateRange} • ${getBucketLabel()}`;
    };

    const handleSearch = useCallback(async () => {
        if (!selectedProduct) return;

        setIsLoading(true);
        setCoverage(null);
        try {
            let dailyRows = [];

            if (selectedProduct === 'all') {
                const topicIds = Object.keys(topicMap);
                // Same cross-topic double-count caveat as the engagement chart:
                // a multi-topic post's engagement counts toward each topic.
                const results = await Promise.all(
                    topicIds.map(async (topicId) => {
                        try {
                            const response = await apiPost(endpoint, {
                                topic_id: topicId,
                                start_date: startDate,
                                end_date: endDate,
                                ...(sourceFilter ? { source: sourceFilter } : {}),
                            });
                            if (!response.ok) throw new Error(`Backend error: ${response.status}`);
                            const json = await response.json();
                            return { data: json.data ?? [], coverage: json.coverage ?? { has_data: false } };
                        } catch (error) {
                            console.error(`Error fetching sentiment data for topic ${topicId}:`, error);
                            return { data: [], coverage: { has_data: false } };
                        }
                    })
                );

                const byDay = {};
                results.forEach(({ data: rows }) => {
                    (rows || []).forEach(item => {
                        if (!byDay[item.day]) byDay[item.day] = { day: item.day, positive: 0, negative: 0 };
                        byDay[item.day].positive += Number(item[positiveField]) || 0;
                        byDay[item.day].negative += Number(item[negativeField]) || 0;
                    });
                });
                dailyRows = Object.values(byDay).map(r => ({ fullDate: r.day, positive: r.positive, negative: r.negative }));
                setCoverage({ has_data: results.some(r => r.coverage?.has_data) });
            } else {
                const response = await apiPost(endpoint, {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    ...(sourceFilter ? { source: sourceFilter } : {}),
                });
                if (!response.ok) throw new Error(`Backend error: ${response.status}`);
                const json = await response.json();
                setCoverage(json.coverage ?? { has_data: false });
                dailyRows = (json.data ?? []).map(r => ({
                    fullDate: r.day,
                    positive: Number(r[positiveField]) || 0,
                    negative: Number(r[negativeField]) || 0,
                }));
            }

            if (!Array.isArray(dailyRows) || dailyRows.length === 0) {
                setData([]);
                return;
            }

            dailyRows.sort((a, b) => parseISO(a.fullDate) - parseISO(b.fullDate));

            const bucketed = bucketSentiment(
                dailyRows.map(r => ({ ...r, date: format(parseISO(r.fullDate), 'MMM dd') })),
                bucketPeriod
            );

            // Render shape: positive as-is (above axis), negative negated (below axis).
            let chartRows = bucketed.map(b => ({
                date: b.date,
                fullDate: b.fullDate,
                positive: b.positive,
                negativeNeg: -Math.abs(b.negative),
            }));

            // Zero-fill empty buckets (opt-in) so every bucket in range gets a slot.
            if (fillEmptyBuckets) {
                chartRows = fillBucketGaps(chartRows, startDate, endDate, bucketPeriod, { positive: 0, negativeNeg: 0 });
            }

            setData(chartRows);
        } catch (error) {
            console.error('Error in sentiment fetchData:', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProduct, startDate, endDate, bucketPeriod, topicMap, endpoint, positiveField, negativeField, sourceFilter, fillEmptyBuckets]);

    const autoSearchedRef = useRef(false);
    useEffect(() => {
        if (autoSearch && selectedProduct && !autoSearchedRef.current) {
            autoSearchedRef.current = true;
            handleSearch();
        }
    }, [autoSearch, selectedProduct, handleSearch]);

    // Embedded mode: auto-fetch whenever the shared inputs change.
    useEffect(() => {
        if (embedded && selectedProduct) handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, selectedProduct, startDate, endDate, bucketPeriod, fillEmptyBuckets]);

    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', paddingRight: '24px !important' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0', transition: 'border-color 0.15s ease, box-shadow 0.15s ease' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' }
    };
    const filterLabelStyle = { fontSize: '9px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' };
    const dateInputStyle = { padding: '6px 10px', borderRadius: '4px', border: '1px solid #d0d0d0', fontSize: '13px', height: CONTROL_HEIGHT, boxSizing: 'border-box', transition: 'border-color 0.15s ease', outline: 'none', width: '100%' };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa', borderRadius: '8px', padding: '20px 24px 18px', marginBottom: '20px',
                    boxShadow: isHeaderHovered ? '0 4px 12px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)', transition: 'box-shadow 0.2s ease', maxWidth: '100%', boxSizing: 'border-box'
                }}
            >
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#777', fontWeight: 500, lineHeight: 1.2, marginBottom: '1px' }}>
                        {companyName || 'Company'}
                    </div>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0, color: '#111', lineHeight: 1.25 }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>
                    {selectedProduct && (
                        <div style={{ fontSize: '12px', color: '#999', lineHeight: 1.3, marginTop: '2px' }}>
                            {buildSubline()}
                        </div>
                    )}
                    {coverage?.has_data && (
                        <div style={{ fontSize: '11px', color: '#b58a00', lineHeight: 1.3, marginTop: '4px' }}>
                            Posts within 90 days may rise as posts continue to be rechecked.
                        </div>
                    )}
                    {note && (
                        <div style={{ fontSize: '11px', color: '#777', lineHeight: 1.3, marginTop: '4px', fontStyle: 'italic' }}>
                            {note}
                        </div>
                    )}
                </div>

                {!embedded && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: `${CONTROL_GAP}px`, flexWrap: 'wrap', rowGap: '12px' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector topicMap={filteredTopicMap} selectedId={selectedProduct || ''} onSelect={setSelectedProduct} label="" size="small" sx={selectStyle} />
                    </div>
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>Start Date</div>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={endDate} style={dateInputStyle} />
                    </div>
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={new Date().toISOString().split('T')[0]} style={dateInputStyle} />
                    </div>
                    <div style={{ flex: '1 1 auto', minWidth: 80, maxWidth: 110 }}>
                        <div style={filterLabelStyle}>Bucket</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select value={bucketPeriod} onChange={(e) => setBucketPeriod(e.target.value)}>
                                {BUCKET_PERIOD_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                        <Button variant="contained" onClick={handleSearch} disabled={!selectedProduct || isLoading} startIcon={<SearchIcon />}
                            sx={{ height: CONTROL_HEIGHT, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
                            {isLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </div>
                </div>
                )}
            </div>

            {coverage && !coverage.has_data ? (
                <div style={{
                    marginTop: '8px', width: '100%', height: '200px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', textAlign: 'center', color: '#777', fontSize: '14px',
                    backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    Engagement not yet tracked for this topic — it hasn't been checked by the recheck pipeline yet.
                </div>
            ) : (
                <div style={{ marginTop: '8px', width: '100%', height: `${chartHeight}px` }}>
                    <ResponsiveContainer>
                        <ComposedChart data={data} stackOffset="sign" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} />
                            <YAxis
                                label={{ value: `${metricNoun} (positive ↑ / negative ↓)`, angle: -90, position: 'insideLeft', dx: -10, style: { textAnchor: 'middle' } }}
                                tickFormatter={(v) => compactYAxis ? formatCompactNumber(Math.abs(v)) : Math.abs(v)}
                            />
                            <Tooltip formatter={(value, name) => [compactYAxis ? formatFullNumber(Math.abs(value)) : Math.abs(value), name]} />
                            <Legend />
                            <ReferenceLine y={0} stroke="#888" />
                            <Bar dataKey="positive" stackId="sentiment" fill={POSITIVE_COLOR} name="Positive (NPS 7-10)" />
                            <Bar dataKey="negativeNeg" stackId="sentiment" fill={NEGATIVE_COLOR} name="Negative (NPS 0-3)" />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default PostSentimentEngagementAnalytics;
